import { Layers } from 'lucide-react';
import type { UseQueryResult } from '@tanstack/react-query';

import { ErrorNotice, PanelCard, SkeletonLines, StatRow } from './primitives';
import {
  CHAINS,
  formatInt,
  formatSatsAsBtc,
  formatVsize,
  type ChainId,
  type MempoolInfo,
} from '@/lib/mempool';

export function MempoolCard({
  chainId,
  query,
}: {
  chainId: ChainId;
  query: UseQueryResult<MempoolInfo>;
}) {
  const chain = CHAINS[chainId];
  const { data, isPending, isError, error } = query;

  // The fee histogram is ordered high → low; the first band approximates
  // what is bidding for the next block.
  const nextBlockFee = data?.fee_histogram?.[0]?.[0];
  // Rough number of blocks worth of transactions waiting (4 MvB per block).
  const backlogBlocks = data ? data.vsize / 4_000_000 : undefined;

  return (
    <PanelCard
      icon={<Layers className={`size-4 ${chain.classes.text}`} />}
      title="Mempool"
    >
      {isPending ? (
        <SkeletonLines rows={4} />
      ) : isError ? (
        <ErrorNotice message={error.message} />
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className={`font-mono text-3xl font-semibold tracking-tight ${chain.classes.text}`}>
              {formatInt(data.count)}
            </span>
            <span className="text-sm text-muted-foreground">unconfirmed transactions</span>
          </div>

          <div className="space-y-2">
            <StatRow label="Virtual size" value={formatVsize(data.vsize)} />
            <StatRow
              label="Backlog"
              value={backlogBlocks !== undefined ? backlogBlocks.toFixed(1) : '—'}
              sub="blocks"
            />
            <StatRow label="Total fees waiting" value={formatSatsAsBtc(data.total_fee)} />
            <StatRow
              label="Top of next block"
              value={nextBlockFee !== undefined ? `≥ ${nextBlockFee.toFixed(2)}` : '—'}
              sub="sat/vB"
            />
          </div>
        </div>
      )}
    </PanelCard>
  );
}
